import React from "react";
import moment from "moment";
import groupBy from "lodash/groupBy";
import { Form, Row, Space } from "@masterteam/mt-design-react";
import { useIntl, FormattedMessage } from "react-intl";
import { useSelector } from "react-redux";

import api from "../../providers/api";

import Collapse from "../Collapse";
import KPIMeasuresField from "../KPIMeasuresField";
import CustomKPIMeasures from "../CustomKPIMeasures";

import {
  getLookupName,
  formatDate,
  UnitOfMeasure,
  addToast
} from "../../helpers";

import { ReactComponent as ChangeKpiMeasurementsIcon } from "../../assets/images/change-kpi-measurements.svg";

import "./style.scss";

const { Field } = Form;

const ChangeKPITarget = ({
  kpiDetails,
  isReadOnly,
  lists,
  values,
  setFieldValue
}) => {
  const intl = useIntl();
  const isAr = intl.locale === "ar";

  const theme = useSelector(({ theme }) => theme);

  const kpiMeasures = kpiDetails?.KPIMeasures?.map((item) => ({
    ...item,
    DueDate: moment(item.DueDate),
    Year: new Date(item.DueDate).getFullYear()
  }));

  const list = groupBy(kpiMeasures, "Year");

  const customKpiMeasures = kpiDetails?.KPIMeasures?.map((item) => ({
    ...item,
    DueDate: new Date(item.DueDate),
    Year: new Date(item.DueDate).getFullYear()
  }));

  const generateNewMeasures = async (body) => {
    try {
      const res = await api.kpi.generateMeasures(body);
      setFieldValue(
        "KPIMeasures",
        res.Data?.map((item, i) => ({
          ...item,
          ItemIndex: i,
          DueDate: moment(item.DueDate).locale("en").format("MM-DD-YYYY"),
          Year: new Date(item.DueDate).getFullYear()
        }))
      );
    } catch (err) {
      addToast.error(err.message, {
        toastId: "get-users-error"
      });
    }
  };

  return (
    <Collapse
      defaultExpanded
      title={intl.formatMessage({
        id: "change-kpi-measures"
      })}
      icon={<ChangeKpiMeasurementsIcon />}
    >
      <Space direction="vertical" size="large">
        <Space className="section" direction="vertical">
          <Space className="title-wrapper" align="center" size="medium">
            <div className="title">
              <FormattedMessage id="change-request.current-values" />
            </div>
            <hr />
          </Space>

          <Space direction="vertical" size="large" className="body">
            <Row size="large">
              <Field
                label={intl.formatMessage({ id: "kpi.start-date" })}
                inputProps={{
                  placeholder: intl.formatMessage({ id: "kpi.start-date" }),
                  value: formatDate(kpiDetails?.StartDate)
                }}
                hideErrorMessage
                disabled
              />

              <Field
                label={intl.formatMessage({ id: "kpi.baseline" })}
                inputProps={{
                  value: kpiDetails?.Baseline
                }}
                hideErrorMessage
                disabled
              />
            </Row>
            <Row size="large">
              <Field
                label={intl.formatMessage({ id: "common.baseline-date" })}
                inputProps={{
                  placeholder: intl.formatMessage({
                    id: "kpi-info.kpi-frequency"
                  }),
                  value: formatDate(kpiDetails?.BaselineDate)
                }}
                hideErrorMessage
                disabled
              />

              <Field
                label={intl.formatMessage({ id: "kpi-info.kpi-frequency" })}
                inputProps={{
                  placeholder: intl.formatMessage({
                    id: "kpi-info.kpi-frequency"
                  }),
                  value: getLookupName(
                    theme.Frequencies.item.Items,
                    kpiDetails?.Frequency,
                    isAr
                  )
                }}
                hideErrorMessage
                disabled
              />
            </Row>
            <Row size="large" align="end">
              {kpiDetails?.Frequency === "custom" ? (
                <Field
                  label={intl.formatMessage({ id: "kpi.custom-frequency" })}
                  inputProps={{
                    placeholder: intl.formatMessage({
                      id: "kpi.custom-frequency"
                    }),
                    value: kpiDetails?.CustomFrequency
                  }}
                  disabled
                  hideErrorMessage
                />
              ) : (
                <Field
                  label={intl.formatMessage({ id: "kpi.number-of-years" })}
                  inputProps={{
                    placeholder: intl.formatMessage({
                      id: "kpi.number-of-years"
                    }),
                    value: kpiDetails?.Years
                  }}
                  disabled
                  hideErrorMessage
                />
              )}
              <Row size="large" align="end">
                <Field
                  type="checkbox"
                  label={intl.formatMessage({
                    id: "kpi-info.no-need-target"
                  })}
                  inputProps={{
                    highContrast: true,
                    placeholder: intl.formatMessage({
                      id: "kpi-info.no-need-target"
                    }),
                    value: kpiDetails?.HasNoTarget,
                    checked: kpiDetails?.HasNoTarget
                  }}
                  hideLabel
                  disabled
                  hideErrorMessage
                />

                <Field
                  type="checkbox"
                  label={intl.formatMessage({
                    id: "kpi-info.no-need-baseline"
                  })}
                  inputProps={{
                    highContrast: true,
                    placeholder: intl.formatMessage({
                      id: "kpi-info.no-need-baseline"
                    }),
                    value: kpiDetails?.HasNoBaseline,
                    checked: kpiDetails?.HasNoBaseline
                  }}
                  hideLabel
                  disabled
                  hideErrorMessage
                />
              </Row>
            </Row>

            <Space direction="vertical" size="medium">
              <div className="target-title">
                <FormattedMessage id="change-request.kpi-targets" />
              </div>

              {kpiDetails?.Frequency === "custom" ? (
                <CustomKPIMeasures isReadOnly kpiMeasures={customKpiMeasures} />
              ) : kpiDetails.Years ? (
                <KPIMeasuresField
                  kpiMeasures={list}
                  isReadOnly
                  frequency={kpiDetails?.Frequency}
                />
              ) : null}
            </Space>
          </Space>
        </Space>

        <Space className="section" direction="vertical">
          <Space
            className="updated-values-title-wrapper"
            align="center"
            size="medium"
          >
            <div className="title ">
              <FormattedMessage id="change-request.values-after-change" />
            </div>
            <hr />
          </Space>

          <Space direction="vertical" size="large" className="body">
            <Row size="large">
              <Field
                type="date"
                name="StartDate"
                disabled={isReadOnly}
                label={intl.formatMessage({ id: "kpi.start-date" })}
                inputProps={{
                  placeholder: intl.formatMessage({ id: "kpi.start-date" }),
                  onChange: (e) => {
                    setFieldValue("StartDate", e);
                    generateNewMeasures({
                      KPIID: kpiDetails?.ID,
                      startDate: moment(e).locale("en").format("MM-DD-YYYY"),
                      frequency: values?.Frequency?.Value,
                      Years: values?.Years
                    });
                  }
                }}
                hideErrorMessage
                required
              />

              <Field
                name="Baseline"
                disabled={
                  isReadOnly || values?.HasNoTarget || values?.HasNoBaseline
                }
                id="baseline"
                type="number"
                label={intl.formatMessage({ id: "kpi.baseline" })}
                inputProps={{
                  min: 0,
                  step: "any",
                  onKeyDown: (evt) => {
                    if (evt.key === "e" || evt.key === "-" || evt.key === "+") {
                      evt.preventDefault();
                    }
                  },
                  onChange: (event) => {
                    setFieldValue("Baseline", event.target.value);
                  },
                  placeholder: `${intl.formatMessage({
                    id: "kpi.baseline"
                  })} ${UnitOfMeasure(
                    values.UnitOfMeasure && values.UnitOfMeasure?.value
                  )}`
                }}
                hideErrorMessage
                required
              />
            </Row>
            <Row size="large">
              <Field
                type="date"
                label={intl.formatMessage({ id: "common.baseline-date" })}
                name="BaselineDate"
                disabled={isReadOnly}
                hideErrorMessage
                required
              />

              <Field
                type="select"
                name="Frequency"
                disabled={isReadOnly}
                label={intl.formatMessage({ id: "kpi-info.kpi-frequency" })}
                inputProps={{
                  options: theme.Frequencies.item.Items,
                  getOptionLabel: (el) => (isAr ? el.Arabic : el.English),
                  getOptionValue: (el) => el.Value,
                  placeholder: intl.formatMessage({
                    id: "kpi-info.kpi-frequency"
                  }),
                  onChange: (e) => {
                    setFieldValue("Frequency", e);

                    if (e?.Value === "custom") {
                      setFieldValue("KPIMeasures", []);
                      return;
                    }

                    generateNewMeasures({
                      KPIID: kpiDetails?.ID,
                      startDate: moment(values?.StartDate)
                        .locale("en")
                        .format("MM-DD-YYYY"),
                      frequency: e?.Value,
                      Years: values?.Years
                    });
                  }
                }}
                hideErrorMessage
                required
              />
            </Row>
            {values?.Frequency?.Value === "custom" ? (
              <Field
                name="CustomFrequency"
                label={intl.formatMessage({ id: "kpi.custom-frequency" })}
                inputProps={{
                  placeholder: intl.formatMessage({
                    id: "kpi.custom-frequency"
                  })
                }}
                disabled={isReadOnly}
                hideErrorMessage
                required
              />
            ) : (
              <Row size="large" align="end">
                <Field
                  type="number"
                  name="Years"
                  label={intl.formatMessage({ id: "kpi.number-of-years" })}
                  inputProps={{
                    placeholder: intl.formatMessage({
                      id: "kpi.number-of-years"
                    }),
                    min: "1",
                    max: "5",
                    onChange: (e) => {
                      const { value } = e.target;

                      if (!value) {
                        setFieldValue("Years", kpiDetails?.Years);

                        generateNewMeasures({
                          KPIID: kpiDetails?.ID,
                          startDate: moment(values.StartDate)
                            .locale("en")
                            .format("MM-DD-YYYY"),
                          frequency: values?.Frequency?.Value,
                          Years: e.target?.value
                        });

                        return;
                      }

                      setFieldValue("Years", value);
                      generateNewMeasures({
                        KPIID: kpiDetails?.ID,
                        startDate: moment(values.StartDate)
                          .locale("en")
                          .format("MM-DD-YYYY"),
                        frequency: values?.Frequency?.Value,
                        Years: e.target?.value
                      });
                    }
                  }}
                  disabled={isReadOnly}
                  hideErrorMessage
                  required
                />

                <Row size="large" align="end">
                  <Field
                    name="HasNoTarget"
                    type="checkbox"
                    label={intl.formatMessage({
                      id: "kpi-info.no-need-target"
                    })}
                    inputProps={{
                      onChange: (val) => {
                        if (values.HasNoBaseline)
                          setFieldValue("HasNoBaseline", false);

                        setFieldValue("HasNoTarget", !values.HasNoTarget);

                        if (!values.HasNoTarget) {
                          setFieldValue("Baseline", null);
                          setFieldValue(
                            "KPIMeasures",
                            values.KPIMeasures?.map((e) => {
                              return { ...e, Target: null };
                            })
                          );
                        }
                      },
                      placeholder: intl.formatMessage({
                        id: "kpi-info.no-need-target"
                      })
                    }}
                    hideLabel
                    disabled={isReadOnly}
                    hideErrorMessage
                  />

                  <Field
                    name="HasNoBaseline"
                    type="checkbox"
                    label={intl.formatMessage({
                      id: "kpi-info.no-need-baseline"
                    })}
                    inputProps={{
                      onChange: (val) => {
                        if (values.HasNoTarget)
                          setFieldValue("HasNoTarget", false);

                        setFieldValue("HasNoBaseline", !values.HasNoBaseline);

                        if (!values.HasNoBaseline) {
                          setFieldValue("Baseline", null);
                          setFieldValue(
                            "KPIMeasures",
                            values.KPIMeasures?.map((e) => {
                              return { ...e, Target: null };
                            })
                          );
                        }
                      },
                      placeholder: intl.formatMessage({
                        id: "kpi-info.no-need-baseline"
                      })
                    }}
                    hideLabel
                    disabled={isReadOnly}
                    hideErrorMessage
                  />
                </Row>
              </Row>
            )}

            <Space direction="vertical" size="medium">
              <div className="target-title">
                <FormattedMessage id="change-request.kpi-targets" />
              </div>
              {values?.Frequency?.Value === "custom" ? (
                <CustomKPIMeasures isReadOnly={isReadOnly} />
              ) : values.Years ? (
                <KPIMeasuresField
                  isChangeKpiTarget={isReadOnly}
                  disabledMeasures={
                    isReadOnly || values?.HasNoTarget || values?.HasNoBaseline
                  }
                />
              ) : null}
            </Space>
          </Space>
        </Space>
      </Space>
    </Collapse>
  );
};

export default ChangeKPITarget;
